import { useAuth } from '../hooks/useAuth'
import { useState, useEffect } from 'react'
import { fetchRows, fetchCrewDb } from '../lib/api'
import FleetMap from '../components/FleetMap'
import FleetCalendar from '../components/FleetCalendar'
import CrewLogins from './CrewLogins'
import ApprovalsCard, { rememberOpen } from '../components/ApprovalsCard'
import GettingStarted from '../components/GettingStarted'
import { supabase } from '../lib/supabase'
import { featureOn } from '../lib/features'
import { drillsNeedingAttention } from '../lib/drillTypes'
import { vesselHealth, worstFirst, TONE_COLOR } from '../lib/fleetCore'
import { expiryStatus, expiryToday } from '../lib/expiry'
import { vesselCompliance } from '../lib/fleetCompliance'
import { needsLines, expiringDocs, alertRows, SHOW_FIRST } from '../lib/fleetToday'
import { fleetTabsFor } from '../lib/navGroups'
import FleetCompliance from '../components/FleetCompliance'
import FleetReportButton from '../components/FleetReportButton'
import { tutorialMark } from '../lib/tutorialStore'
import FleetStandards from '../components/FleetStandards'
import FleetTeam from '../components/FleetTeam'
import { memberCan, cleanRights } from '../lib/companyMembers'
import { trendFrom, arrowOf } from '../lib/readinessTrend'
import { isSimple } from '../service/simpleMode'
import ServiceBoard from '../service/ServiceBoard'
import { useFleetGroups, useFleetJobsToday, inFleetGroup } from '../service/FleetGroups'

function getVal(v) {
  if (!v) return ''
  if (typeof v === 'object' && !Array.isArray(v)) return v.value || v.name || v.label || ''
  if (Array.isArray(v)) return v.map(getVal).filter(Boolean).join(', ')
  return String(v)
}
const NAME_KEYS = ['Part Name', 'Task Name', 'Task', 'Title', 'Document Name', 'Document', 'Certificate', 'Name', 'Equipment Name', 'Equipment', 'Item Name', 'Item', 'Description']
function nameOf(r) {
  for (const k of NAME_KEYS) { const v = getVal(r[k]); if (v) return v }
  for (const k of Object.keys(r)) { if (/name|title|task|item|equipment|document|certif|descr/i.test(k)) { const v = getVal(r[k]); if (v) return v } }
  return 'Untitled'
}
function dateOf(r, re) {
  for (const k of Object.keys(r)) { if (re.test(k)) { const v = getVal(r[k]); if (v && !isNaN(new Date(v))) return v } }
  return ''
}
function fmtDate(v) {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d)) return v
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
const tsOf = v => { const d = new Date(v); return isNaN(d) ? Infinity : d.getTime() }
const localToday = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
// the app's blue / white: card, text and lines follow the app's colours; accents are plain colours because some views add an opacity to them
const C = { card: 'var(--card)', line: 'var(--line)', text: 'var(--text)', muted: 'var(--muted)', faint: '#8a97ab', blue: '#1B4FD8', green: '#1e7d43', red: '#B4313C', amber: '#9a6700', purple: '#6b4fc9' }
const TONE = { red: C.red, amber: C.amber, blue: C.blue, muted: C.faint }

const CSS = `
.hcf{color:${C.text};max-width:1500px;margin:0 auto;padding:4px 4px 40px}
.hcf-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:18px;min-width:0}
.hcf-sec{margin-bottom:16px}
.hcf-top{display:flex;align-items:center;flex-wrap:wrap;gap:10px 12px;margin-bottom:16px}
.hcf-hello{font-size:19px;font-weight:700;letter-spacing:-.01em;margin-right:auto;white-space:nowrap}
.hcf-hello span{font-size:13px;font-weight:500;color:${C.muted};margin-left:10px}
.hcf-filter{height:36px;max-width:220px;padding:0 10px;border:1px solid ${C.line};border-radius:10px;background:${C.card};color:${C.text};font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer}
.hcf-brief{display:inline-flex;align-items:center;justify-content:center;gap:7px;height:36px;background:${C.card};border:1px solid ${C.line};color:${C.blue};border-radius:10px;padding:0 14px;font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcf-brief:hover{border-color:${C.blue}}
.hcf-hero{display:grid;grid-template-columns:minmax(0,45fr) minmax(0,55fr);gap:16px;margin-bottom:16px}
.hcf-hero>*{height:420px}
.hcf-mapcard{padding:0;overflow:hidden}
.hcf-rank{display:flex;flex-direction:column}
.hcf-h{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;gap:10px;min-height:22px}
.hcf-h b{font-size:15px;font-weight:700}
.hcf-sub{font-size:12px;color:${C.muted};margin-top:2px}
.hcf-ell{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hcf-scroll{flex:1;min-height:0;overflow-y:auto;padding-right:2px}
.hcf-tr{display:grid;align-items:center;gap:10px;padding:10px 8px;border-top:1px solid ${C.line};cursor:pointer;transition:background .12s}
.hcf-tr:hover,.hcf-tr.hl{background:rgba(27,79,216,.05)}
.hcf-tr.sel{background:rgba(27,79,216,.09);box-shadow:inset 3px 0 0 ${C.blue}}
.hcf-tr:focus-visible{outline:2px solid ${C.blue};outline-offset:-2px}
.hcf-open{background:transparent;border:1px solid ${C.line};color:${C.blue};border-radius:8px;min-width:34px;height:32px;padding:0 8px;cursor:pointer;font:600 12.5px/1 inherit;font-family:inherit;white-space:nowrap}
.hcf-open:hover{border-color:${C.blue}}
.hcf-ov{grid-template-columns:minmax(0,1fr) auto 34px}
.hcf-vc{display:grid;grid-template-columns:38px minmax(0,1fr);gap:10px;align-items:center;min-width:0}
.hcf-vn{font-size:13.5px;font-weight:600;line-height:1.25;overflow-wrap:anywhere}
.hcf-why{font-size:12px;color:${C.muted};margin-top:2px;line-height:1.35}
.hcf-ring{position:relative;width:38px;height:38px;flex-shrink:0}
.hcf-ring b{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;font-variant-numeric:tabular-nums}
.hcf-trend{font-size:11px;font-weight:700;font-variant-numeric:tabular-nums;color:${C.muted};white-space:nowrap}
.hcf-trend.down{color:${C.amber}}.hcf-trend.up{color:${C.green}}
.hcf-line{display:grid;grid-template-columns:44px minmax(0,1fr) auto;align-items:center;gap:12px;padding:11px 0;border-top:1px solid ${C.line}}
.hcf-count{font-size:20px;font-weight:700;font-variant-numeric:tabular-nums;text-align:center}
.hcf-line b{font-size:13.5px;font-weight:600}
.hcf-more{padding:0 0 8px 56px}
.hcf-row{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:12px;padding:8px 0;border-top:1px solid ${C.line}}
.hcf-row b{display:block;font-size:13px;font-weight:600}
.hcf-showall{display:block;background:transparent;border:none;color:${C.blue};font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer;padding:12px 0 2px}
.hcf-link{background:transparent;border:none;color:${C.muted};font:500 12px/1 inherit;font-family:inherit;cursor:pointer;padding:0}
.hcf-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:16px}
.hcf-kpi{padding:12px 16px}
.hcf-kpi div{font-size:12px;color:${C.muted};line-height:1.2}
.hcf-kpi b{display:block;font-size:20px;font-weight:700;line-height:1.2;margin-top:4px;font-variant-numeric:tabular-nums}
.hcf-alert{grid-template-columns:8px minmax(0,1fr) 12px;padding:10px 0}
.hcf-alert i{width:8px;height:8px;border-radius:50%;display:block}
.hcf-alert b{display:block;font-size:13px;font-weight:600}
.hcf-alert em{font-style:normal;color:${C.faint};font-size:15px}
.hcf .hc-gs{margin:0;padding:10px 18px}
@media (max-width:1023px){.hcf-hero{grid-template-columns:minmax(0,1fr)}.hcf-hero>*{height:auto}.hcf-mapcard{height:320px}.hcf-scroll.hcf-all{max-height:none;overflow:visible}}
@media (max-width:600px){.hcf-mapcard{height:260px}.hcf-hello{width:100%}.hcf-filter{flex:1 1 100%;max-width:none}.hcf-top>span,.hcf-top>.hcf-brief{flex:1 1 40%}.hcf-top .hcf-brief{width:100%}.hcf-kpis{gap:8px}.hcf-kpi{padding:10px}.hcf-kpi div{font-size:11px}.hcf-more{padding-left:0}.hcf-sec,.hcf-hero,.hcf-kpis{margin-bottom:12px}.hcf-card:not(.hcf-kpi):not(.hcf-mapcard){padding:14px}}
`

// the Dashboard's readiness ring, small (fleet-1): same score (src/lib/readiness.js), same green / amber / red
function Ring({ score, tone }) {
  const R = 15.5, L = 2 * Math.PI * R, col = TONE_COLOR[tone] || C.faint
  return (
    <span className="hcf-ring" role="img" aria-label={score == null ? 'Readiness: checking' : 'Readiness ' + score + ' of 100'}>
      <svg width="38" height="38" viewBox="0 0 38 38"><circle cx="19" cy="19" r={R} fill="none" stroke={C.line} strokeWidth="4" />
        {score != null && <circle cx="19" cy="19" r={R} fill="none" stroke={col} strokeWidth="4" strokeLinecap="round" strokeDasharray={L} strokeDashoffset={L * (1 - score / 100)} transform="rotate(-90 19 19)" />}</svg>
      <b style={{ color: score == null ? C.faint : C.text }}>{score == null ? '…' : score}</b>
    </span>
  )
}
// fleet-4: the change vs 7 days ago on the row; 30 days and the numbers on hover (src/lib/readinessTrend.js)
function Trend({ t }) {
  if (!t || !t.d7) return null   // no score from 7 days ago yet: nothing to say
  const a7 = arrowOf(t.d7)
  const say = (x, n) => (x ? (x.delta > 0 ? '+' : '') + x.delta + ' vs ' + n + ' days ago (' + x.from + ')' : 'no score from ' + n + ' days ago yet')
  return <span className={'hcf-trend ' + a7.tone} title={'Readiness trend: ' + say(t.d7, 7) + ' · ' + say(t.d30, 30)} aria-label={'Readiness trend: ' + say(t.d7, 7) + ', ' + say(t.d30, 30)}>{a7.arrow} {a7.text} <span style={{ fontWeight: 500 }}>7d</span></span>
}
const q = p => Promise.resolve(p).then(r => (r && !r.error && r.data) || [], () => [])

// the first few, then "Show all"
function ShowAll({ items, render }) {
  const [all, setAll] = useState(false)
  return <>
    {(all ? items : items.slice(0, SHOW_FIRST)).map(render)}
    {items.length > SHOW_FIRST && <button className="hcf-showall" onClick={() => setAll(a => !a)}>{all ? 'Show less' : 'Show all ' + items.length}</button>}
  </>
}

export default function Fleet({ onOpenVessel, tab: chosenTab = 'overview' }) {
  const { config, rawConfig } = useAuth()
  const fleet = config?.fleet || []
  // a company team member (fleet-4): their vessels (the database gives only those) and their areas; the main login: everything
  const member = rawConfig?.member || null
  const rights = member ? cleanRights(member.rights) : null
  const mayApprove = !member || rights.approvals === 'full'
  const [snaps, setSnaps] = useState(null)   // readiness_snapshots of the fleet, last 36 days (null = not read yet / no table)
  const [compliance, setCompliance] = useState({})
  const [positions, setPositions] = useState({})
  const [hover, setHover] = useState(null)
  const [sel, setSel] = useState(null)       // the vessel picked on the map or in the ranking: highlighted in both. Filters nothing.
  const [filter, setFilter] = useState('')   // the one filter: '' = all vessels, 'v:<id>' = a vessel, 'g:<id>' = a group (Service package)
  const [openLine, setOpenLine] = useState(null)   // the line of Needs you today that is open
  const [appr, setAppr] = useState(null)     // what the approvals card knows: { count, total, notes, openNote, markAllRead }
  const serviceOn = !!config?.service?.on, fleetGroups = useFleetGroups(serviceOn)
  // the views this login has (left menu, src/lib/navGroups.js); a view it does not have shows the Overview
  const tab = fleetTabsFor(rawConfig || config, member).some(t => t.fleetTab === chosenTab) ? chosenTab : 'overview'
  useEffect(() => { if (tab === 'compliance') tutorialMark('compliance') }, [tab])
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60 * 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function run() {
      for (const v of fleet) {
        try {
          // that vessel's plan decides what counts, as on its own Dashboard (a management login has Full on every module)
          const see = m => featureOn(rawConfig || config, v, m) && memberCan(member, m)
          let [tasks, docs, crewDocs, eq, inv, drillRecs, ras, ism, sops, crew] = await Promise.all([
            fetchRows('mtask', v.id).catch(() => []),
            fetchRows('docs', v.id).catch(() => []),
            fetchRows('crew_docs', v.id).catch(() => []),
            fetchRows('equipment', v.id).catch(() => []),
            fetchRows('inventory', v.id).catch(() => []),
            see('checklists') ? q(supabase.from('drill_records').select('drill_type, drill_date').eq('client_id', v.id)) : [],
            see('risk') ? q(supabase.from('risk_assessments').select('ra_number,activity,status,next_review_due').eq('client_id', v.id).limit(1000)) : [],
            see('ism_audits') ? q(supabase.from('ism_findings').select('id,ref,grade,status,element,description,due_date').eq('client_id', v.id).neq('status', 'closed')) : [],
            // fleet-2 Compliance tab: SOP reviews, and the crew names for the crew certificates
            see('manuals') ? q(supabase.from('manuals').select('id,title,doc_number,review_due,status,archived_at').eq('client_id', v.id).eq('doc_kind', 'sop').not('review_due', 'is', null)) : [],
            see('crew') ? fetchCrewDb(v.id, 'id,first_name,last_name,name').catch(() => []) : [],
          ])
          if (isSimple(config?.service, v.id)) { crewDocs = []; drillRecs = []; ism = [] }   // Service package, simple mode: these do not count on this vessel
          const drills = drillsNeedingAttention(drillRecs)
          const vToday = v.timezone ? expiryToday(v.timezone) : localToday()   // that vessel's own date (ship's time zone)
          const health = vesselHealth({ tasks, docs, crewDocs, eq, inv, drills, ras, ismMajor: ism.filter(x => x.grade === 'major_nc').length }, { today: vToday, see })
          const st = r => getVal(r['Status'])
          const overdueL = tasks.filter(t => st(t) === 'Overdue').map(r => ({ name: nameOf(r), date: dateOf(r, /due|next/i) }))
          // expired: the shared rule on the expiry date (src/lib/expiry.js), as the vessel's own Dashboard and lists - never the stored Status
          const expired = r => expiryStatus(r['Expiry Date'], vToday).expired
          const docsL = docs.filter(expired).map(r => ({ name: nameOf(r), date: dateOf(r, /expir/i) }))
          const certsL = crewDocs.filter(expired).map(r => ({ name: nameOf(r), date: dateOf(r, /expir/i) }))
          const oosL = eq.filter(e => st(e) === 'Out of Service').map(r => ({ name: nameOf(r) }))
          const oostockL = inv.filter(i => st(i) === 'Out of Stock').map(r => ({ name: nameOf(r) }))
          const oostock = oostockL.length
          const total = overdueL.length + docsL.length + certsL.length + oosL.length + oostock
          const due30 = health.due.filter(x => x.window === 30).length
          const drillsLateL = drills.filter(x => x.status === 'overdue').map(x => ({ name: x.label + ' drill' }))
          const raLateL = health.raLate.map(r => ({ name: [r.ra_number, r.activity].filter(Boolean).join(' ') || 'Risk assessment', date: r.next_review_due }))
          if (!cancelled) setCompliance(c => ({ ...c, [v.id]: { total, overdue: overdueL.length, expiredDocs: docsL.length, expiredCerts: certsL.length, oos: oosL.length, oostock, overdueL, docsL, certsL, oosL, oostockL, health, due30, dueL: health.due, drillsLateL, raLateL, raw: { docs, crewDocs, crew, drills, ras, ism, sops } } }))
        } catch (e) { /* skip vessel on error */ }
      }
    }
    if (fleet.length) run()
    return () => { cancelled = true }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  // fleet-4: the daily readiness snapshots (written by the 05:00 job) for the 7 / 30-day trend
  useEffect(() => {
    let cancelled = false
    const ids = fleet.map(v => v.id)
    if (!ids.length) return
    const since = new Date(Date.now() - 36 * 864e5).toISOString().slice(0, 10)
    supabase.from('readiness_snapshots').select('client_id,day,score').in('client_id', ids).gte('day', since).order('day').limit(5000)
      .then(({ data, error }) => { if (!cancelled) setSnaps(error ? null : data || []) }, () => {})
    return () => { cancelled = true }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  useEffect(() => {
    let cancelled = false
    const mmsis = fleet.map(v => v.mmsi).filter(Boolean)
    if (!mmsis.length) return
    const mmsiParam = mmsis.join(',')
    async function readCache() {
      try {
        const res = await fetch('/api/workspace?cached=1&mmsi=' + encodeURIComponent(mmsiParam))
        const data = await res.json()
        if (!cancelled && data.positions) setPositions(data.positions)
      } catch (e) {}
    }
    async function collect() {
      try { await fetch('/api/workspace?all=1&mmsi=' + encodeURIComponent(mmsiParam)); if (!cancelled) readCache() } catch (e) {}
    }
    readCache()
    collect()
    const id = setInterval(collect, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line
  const same = (a, b) => String(a) === String(b)
  const filterId = filter.slice(2)
  const scope = filter.startsWith('v:') ? fleet.filter(v => same(v.id, filterId)) : fleet.filter(v => !serviceOn || inFleetGroup(fleetGroups, filter.startsWith('g:') ? filterId : null, v.id))
  const inScope = v => scope.some(x => same(x.id, v.id))
  const pick = id => setSel(cur => (same(cur, id) ? null : id))
  const pickFilter = f => { setFilter(f); setSel(null); setOpenLine(null) }

  const posOf = v => (v.mmsi ? positions[String(v.mmsi)] : null)
  const mapVessels = fleet.filter(v => posOf(v)).map(v => {
    const p = posOf(v)
    return { id: v.id, vessel_name: v.vessel_name, lat: p.lat, lon: p.lon, speed: p.speed, course: p.course, ts: p.ts, total: compliance[v.id]?.total, score: compliance[v.id]?.health?.score, tone: compliance[v.id]?.health?.tone }
  })
  const scopeMap = mapVessels.filter(m => scope.some(v => same(v.id, m.id)))
  const allLoaded = scope.length > 0 && scope.every(v => compliance[v.id])
  const sum = k => scope.reduce((s, v) => s + (compliance[v.id]?.[k] || 0), 0)
  const underway = scopeMap.filter(v => Number(v.speed || 0) > 0.5).length
  const dash = '—'
  const openById = (id, p) => { const v = fleet.find(x => same(x.id, id)); if (v && onOpenVessel) onOpenVessel(v, p) }
  // approval card: open that vessel straight on the PO / APA
  const openItem = r => { const v = fleet.find(x => same(x.id, r.client_id)); if (v && onOpenVessel) onOpenVessel(v, rememberOpen(r.kind, r.open_id, r.title)) }
  // a pick on the map: the row lights up; the list scrolls only inside itself, so a phone stays on the map
  const onMarker = id => {
    pick(id)
    const el = document.getElementById('fleet-row-' + id), box = el && el.closest('.hcf-scroll')
    if (box && box.scrollHeight > box.clientHeight) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const nameOfVessel = id => (fleet.find(x => same(x.id, id)) || {}).vessel_name || ''
  const collect = key => scope.flatMap(v => (compliance[v.id]?.[key] || []).map(it => ({ ...it, vid: v.id })))
  const overdueAll = collect('overdueL').sort((a, b) => tsOf(a.date) - tsOf(b.date))
  const expiredAll = [...collect('docsL').map(x => ({ ...x, kind: 'doc' })), ...collect('certsL').map(x => ({ ...x, kind: 'cert' }))].sort((a, b) => tsOf(a.date) - tsOf(b.date))
  const expiringAll = expiringDocs(collect('dueL')).sort((a, b) => a.days - b.days)
  const jobsToday = useFleetJobsToday(serviceOn, scope)
  const lines = needsLines({ approvals: mayApprove && appr ? appr : null, overdue: overdueAll, expired: expiredAll, expiring: expiringAll, outOfStock: collect('oostockL'), jobs: jobsToday })
  const alerts = alertRows(scope.map(v => ({ id: v.id, vessel_name: v.vessel_name, c: compliance[v.id] })), appr ? appr.notes : [])
  const dueLater = scope.reduce((s, v) => s + (compliance[v.id]?.dueL || []).filter(x => x.window > 30).length, 0)
  const health = Object.fromEntries(fleet.filter(v => compliance[v.id]).map(v => [v.id, compliance[v.id].health]))
  const ranked = worstFirst(fleet, health).filter(inScope)
  // fleet-2 Compliance view + fleet report: every item overdue / due in 90 days, per vessel (that vessel's plan decides what counts)
  const seeOf = v => m => featureOn(rawConfig || config, v, m) && memberCan(member, m)
  const itemsOf = v => (compliance[v.id] && compliance[v.id].raw ? vesselCompliance(v, compliance[v.id].raw, { today: localToday(), see: seeOf(v) }) : [])
  const itemsFor = id => { const v = fleet.find(x => same(x.id, id)); return v ? itemsOf(v) : [] }
  const fleetLoaded = fleet.length > 0 && fleet.every(v => compliance[v.id])
  const complianceItems = tab === 'compliance' ? scope.flatMap(itemsOf) : []
  const trendOf = v => (snaps ? trendFrom(snaps.filter(x => String(x.client_id) === String(v.id)), { today: localToday(), live: compliance[v.id]?.health?.score ?? null }) : null)
  const showReport = !member || memberCan(member, 'reports')
  const briefing = () => window.dispatchEvent(new CustomEvent('hc-assistant-open', { detail: { text: 'Fleet briefing' } }))
  const openJobs = () => window.dispatchEvent(new CustomEvent('hc-fleet-tab', { detail: { tab: 'jobs' } }))

  const hour = new Date().getHours()
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  const quiet = text => <div style={{ color: C.muted, fontSize: 13, padding: '10px 0' }}>{text}</div>
  const toggle = key => setOpenLine(o => (o === key ? null : key))
  const needRow = (key, title, sub, go) => (
    <div key={key} className="hcf-row">
      <div style={{ minWidth: 0 }}><b className="hcf-ell">{title}</b><div className="hcf-sub hcf-ell">{sub}</div></div>
      <button className="hcf-open" onClick={go}>Open</button>
    </div>
  )
  const needLine = l => (
    <div key={l.key} className="hcf-line" data-line={l.key} {...(l.key === 'jobs' ? { 'data-service': 'fleet-jobs' } : {})}>
      <div className="hcf-count" style={{ color: TONE[l.tone] }}>{l.count}</div>
      <div style={{ minWidth: 0 }}><b>{l.label}</b>{l.detail && <div className="hcf-sub">{l.detail}</div>}</div>
      <button className="hcf-open" aria-expanded={l.key === 'jobs' ? undefined : openLine === l.key} onClick={() => (l.key === 'jobs' ? openJobs() : toggle(l.key))}>{l.key !== 'jobs' && openLine === l.key ? 'Close' : l.action}</button>
    </div>
  )
  const lineOf = key => lines.find(l => l.key === key)

  return (
    <div className="hcf">
      <style>{CSS}</style>

      <div className="hcf-top">
        <div className="hcf-hello">{greet}<span>{today}</span></div>
        {(fleet.length > 1 || fleetGroups.groups.length > 0) && (
          <select className="hcf-filter" aria-label="Show" value={filter} onChange={e => pickFilter(e.target.value)}>
            <option value="">All vessels</option>
            {serviceOn && fleetGroups.groups.length > 0 && <optgroup label="Groups" data-service="fleet-groups">{fleetGroups.groups.map(g => <option key={g.id} value={'g:' + g.id}>{g.name}</option>)}</optgroup>}
            <optgroup label="Vessels">{fleet.map(v => <option key={v.id} value={'v:' + v.id}>{v.vessel_name}</option>)}</optgroup>
          </select>
        )}
        {fleet.length > 0 && showReport && <span data-tour="fleet-report" onClickCapture={() => tutorialMark('fleet_report')}><FleetReportButton fleet={fleet} company={config?.companyName || ''} health={health} itemsFor={itemsFor} ready={fleetLoaded} palette={C} /></span>}
        {fleet.length > 0 && <button className="hcf-brief" onClick={briefing} title="The AI assistant: one line per vessel that needs you">Fleet briefing</button>}
      </div>

      {fleet.length === 0 ? (
        <>
          <div className="hcf-card hcf-sec" style={{ color: C.muted, fontSize: 14, padding: 40, textAlign: 'center' }}>No vessels assigned to this company yet.</div>
          <GettingStarted compact />
        </>
      ) : (
        <>
          {tab === 'jobs' && serviceOn ? <ServiceBoard fleet={scope} onOpenVessel={onOpenVessel} config={rawConfig || config} /> : tab === 'overview' ? (<>
          <div className="hcf-hero">
            <div className="hcf-card hcf-mapcard">
              <FleetMap vessels={scopeMap} highlightId={hover ?? sel} focusId={sel} onMarkerClick={onMarker} onOpen={id => openById(id)} height="100%" />
            </div>

            <div className="hcf-card hcf-rank" data-tour="fleet-vessels">
              <div className="hcf-h"><b>Fleet overview</b></div>
              <div className="hcf-scroll hcf-all">
                {ranked.map(v => {
                  const c = compliance[v.id]
                  const h = c && c.health
                  return (
                    <div key={v.id} id={'fleet-row-' + v.id} data-score={h ? h.score : ''}
                      className={'hcf-tr hcf-ov' + (same(sel, v.id) ? ' sel' : same(hover, v.id) ? ' hl' : '')}
                      role="button" tabIndex={0} aria-pressed={same(sel, v.id)}
                      onMouseEnter={() => setHover(v.id)} onMouseLeave={() => setHover(null)} onClick={() => pick(v.id)} onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); pick(v.id) } }}>
                      <div className="hcf-vc">
                        <Ring score={h ? h.score : null} tone={h && h.tone} />
                        <div style={{ minWidth: 0 }}>
                          <div className="hcf-vn">{v.vessel_name}</div>
                          <div className="hcf-why">{h ? h.reason : 'Checking\u2026'}</div>
                        </div>
                      </div>
                      <Trend t={trendOf(v)} />
                      <button className="hcf-open" title={'Open ' + v.vessel_name} aria-label={'Open ' + v.vessel_name} onClick={e => { e.stopPropagation(); openById(v.id) }}>&rarr;</button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <section className="hcf-card hcf-sec" data-needs>
            <div className="hcf-h" style={{ marginBottom: 6 }}><b>Needs you today</b></div>
            {lineOf('approvals') && needLine(lineOf('approvals'))}
            {mayApprove && <ApprovalsCard palette={{ card: C.card, line: C.line, text: C.text, muted: C.muted, accent: C.blue, red: C.red, amber: C.amber, bad: '#a32a35', good: '#0f6446' }} onOpen={openItem} actions embedded expanded={openLine === 'approvals'} onData={setAppr} scopeIds={filter ? scope.map(v => v.id) : null} />}
            {lineOf('overdue') && needLine(lineOf('overdue'))}
            {openLine === 'overdue' && <div className="hcf-more"><ShowAll items={overdueAll} render={(t, i) => needRow(i, t.name, nameOfVessel(t.vid) + (t.date ? ' · due ' + fmtDate(t.date) : ''), () => openById(t.vid, 'mtask'))} /></div>}
            {lineOf('docs') && needLine(lineOf('docs'))}
            {openLine === 'docs' && <div className="hcf-more"><ShowAll items={[...expiredAll.map(d => ({ title: d.name, sub: 'expired ' + fmtDate(d.date), vid: d.vid, page: d.kind === 'cert' ? 'crew' : 'docs' })), ...expiringAll.map(d => ({ title: d.label, sub: fmtDate(d.date), vid: d.vid, page: d.kind === 'crew' ? 'crew' : 'docs' }))]}
              render={(d, i) => needRow(i, d.title, nameOfVessel(d.vid) + ' · ' + d.sub, () => openById(d.vid, d.page))} /></div>}
            {lineOf('stock') && needLine(lineOf('stock'))}
            {openLine === 'stock' && <div className="hcf-more"><ShowAll items={collect('oostockL')} render={(x, i) => needRow(i, x.name, nameOfVessel(x.vid), () => openById(x.vid, 'inventory'))} /></div>}
            {lineOf('jobs') && needLine(lineOf('jobs'))}
            {!allLoaded ? <div style={{ borderTop: lines.length ? '1px solid ' + C.line : 'none' }}>{quiet('Checking…')}</div> : !lines.length && <div style={{ borderTop: '1px solid ' + C.line }}>{quiet('Nothing needs you today.')}</div>}
          </section>

          <div className="hcf-kpis">
            <div className="hcf-card hcf-kpi"><div>Vessels</div><b>{scope.length}</b></div>
            <div className="hcf-card hcf-kpi"><div>Underway</div><b>{scopeMap.length ? underway : dash}</b></div>
            <div className="hcf-card hcf-kpi"><div>Due in 31&ndash;90 days</div><b>{allLoaded ? dueLater : dash}</b></div>
            <div className="hcf-card hcf-kpi"><div>Out of service</div><b>{allLoaded ? sum('oos') : dash}</b></div>
          </div>

          <section className="hcf-card hcf-sec" data-alerts>
            <div className="hcf-h" style={{ marginBottom: 6 }}><b>Alerts &amp; updates</b>{appr && appr.notes.length > 0 && <button className="hcf-link" onClick={appr.markAllRead}>Mark updates read</button>}</div>
            {!allLoaded ? quiet('Checking…') : !alerts.length ? quiet('All clear.') : (
              <ShowAll items={alerts} render={a => (
                <div key={a.key} className="hcf-tr hcf-alert" role="button" tabIndex={0} onClick={() => (a.note ? appr.openNote(a.note) : openById(a.vid))} onKeyDown={e => { if (e.key === 'Enter') (a.note ? appr.openNote(a.note) : openById(a.vid)) }}>
                  <i style={{ background: TONE[a.tone] }} />
                  <div style={{ minWidth: 0 }}><b className="hcf-ell">{a.title}</b><div className="hcf-sub hcf-ell">{[a.vessel, a.date ? fmtDate(a.date) : ''].filter(Boolean).join(' · ')}</div></div>
                  <em aria-hidden="true">&rsaquo;</em>
                </div>
              )} />
            )}
          </section>

          <GettingStarted compact />
          </>) : (
            tab === 'compliance'
              ? <FleetCompliance items={complianceItems} fleet={scope} health={health} company={config?.companyName || ''} palette={C} onOpen={openById} loading={!allLoaded} />
              : tab === 'standards'
              ? <FleetStandards fleet={scope} palette={C} onOpenVessel={onOpenVessel} />
              : tab === 'calendar'
              ? <FleetCalendar fleet={fleet} scope={scope} onPickVessel={id => pickFilter(filter === 'v:' + id ? '' : 'v:' + id)} onOpen={openById} />
              : tab === 'team' && !member
              ? <FleetTeam fleet={fleet} palette={C} />
              : tab === 'crew' && !member
              ? <CrewLogins vessels={scope} />
              : null
          )}
        </>
      )}
    </div>
  )
}
